import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

export interface UiTemplate {
  id: string;
  name: string;
  description: string;
  /** First tab to open when applying */
  entryTab: string;
}

const DEFAULT_TEMPLATES: UiTemplate[] = [
  { id: 'scanner', name: 'Scanner focus', description: 'Data table + legacy flow', entryTab: 'data-table' },
  { id: 'flow-desk', name: 'Flow desk', description: 'Options flow + net flow', entryTab: 'options-flow' },
  { id: 'gamma', name: 'Gamma stack', description: 'Volume / OI & spot gamma', entryTab: 'volume-oi-spot-gamma' },
];

interface TemplateGalleryContextValue {
  templates: UiTemplate[];
  isOpen: boolean;
  open: () => void;
  close: () => void;
  applyTemplate: (template: UiTemplate) => void;
}

const TemplateGalleryContext = createContext<TemplateGalleryContextValue | undefined>(undefined);

export const TemplateGalleryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [templates] = useState<UiTemplate[]>(() => {
    try {
      const raw = localStorage.getItem('mmec_ui_templates');
      if (raw) {
        const parsed = JSON.parse(raw) as UiTemplate[];
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_TEMPLATES;
  });

  const applyTemplate = useCallback((template: UiTemplate) => {
    window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: { tab: template.entryTab } }));
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      templates,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      applyTemplate,
    }),
    [templates, isOpen, applyTemplate]
  );

  return <TemplateGalleryContext.Provider value={value}>{children}</TemplateGalleryContext.Provider>;
};

export function useTemplateGallery(): TemplateGalleryContextValue {
  const ctx = useContext(TemplateGalleryContext);
  if (!ctx) throw new Error('useTemplateGallery must be used within TemplateGalleryProvider');
  return ctx;
}
